import CustomizeIcon from "@atlaskit/icon/core/customize";
import CrossIcon from "@atlaskit/icon/core/cross";
import GrowDiagonalIcon from "@atlaskit/icon/core/grow-diagonal";
import ShrinkDiagonalIcon from "@atlaskit/icon/core/shrink-diagonal";
import {useMediaQuery} from "@mantine/hooks";
import {useEffect, useState} from "react";
import {BottomDrawer} from "~/components/BottomDrawer";
import {
    CalendarCustomizeViewPanelBody,
    CalendarCustomizeViewPanelFooter,
} from "~/components/ottopilotDataViewer/calendarCustomizeViewPanel";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {AsideFullscreenPanel, AsideSidebarPanel} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {AnankeButtonGroup} from "@vesta/ananke/components/anankeButtonGroup";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

const MOBILE_BREAKPOINT = "(max-width: 47.9375em)";

export function CalendarCustomizeViewTrigger<T extends {id: Uuid}>({
    controller,
}: {
    controller: OttopilotDataViewerController<T>;
}) {
    const isMobile = useMediaQuery(MOBILE_BREAKPOINT, false, {getInitialValueInEffect: true});
    const [opened, setOpened] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [ephemeralMetricId, setEphemeralMetricId] = useState(controller.calendarDisplayMetricId);

    const metricsConfig = controller.calendarDisplayMetrics;

    useEffect(() => {
        if (opened === false) {
            setEphemeralMetricId(controller.calendarDisplayMetricId);
        }
    }, [controller.calendarDisplayMetricId, opened]);

    if (metricsConfig === undefined) {
        return null;
    }

    const openPanel = () => {
        setEphemeralMetricId(controller.calendarDisplayMetricId);
        setOpened(true);
    };

    const closePanel = () => {
        setOpened(false);
        setIsFullscreen(false);
    };

    const confirmAndClose = () => {
        controller.setCalendarDisplayMetricId(ephemeralMetricId);
        setOpened(false);
        setIsFullscreen(false);
    };

    const triggerButton = (
        <AnankeIconButton
            type="button"
            appearance="default"
            icon={CustomizeIcon}
            label="Customize view"
            isSelected={opened}
            onClick={() => {
                if (opened === false) {
                    openPanel();
                } else {
                    closePanel();
                }
            }}
        />
    );

    const panelBody = (
        <CalendarCustomizeViewPanelBody
            controller={controller}
            ephemeralMetricId={ephemeralMetricId}
            onEphemeralMetricIdChange={setEphemeralMetricId}
            layout={isMobile === true ? "mobile" : "default"}
        />
    );

    const panelFooter = (
        <CalendarCustomizeViewPanelFooter
            onCancel={closePanel}
            onConfirm={confirmAndClose}
        />
    );

    const panelHeaderRight = (
        <AnankeButtonGroup>
            <AnankeIconButton
                type="button"
                appearance="subtle"
                icon={isFullscreen === true ? ShrinkDiagonalIcon : GrowDiagonalIcon}
                label={isFullscreen === true ? "Shrink panel" : "Expand panel"}
                onClick={() => {
                    setIsFullscreen((previous) => !previous);
                }}
            />
            <AnankeIconButton
                type="button"
                appearance="subtle"
                icon={CrossIcon}
                label="Close"
                onClick={closePanel}
            />
        </AnankeButtonGroup>
    );

    const PanelComponent = isFullscreen === true ? AsideFullscreenPanel : AsideSidebarPanel;

    if (isMobile === true) {
        return (
            <>
                {triggerButton}
                <BottomDrawer
                    open={opened}
                    setOpen={setOpened}
                    title="Customize view"
                    zIndex={100}
                    fitContentOnOpen={true}
                    footer={panelFooter}
                >
                    {panelBody}
                </BottomDrawer>
            </>
        );
    }

    if (opened === false) {
        return triggerButton;
    }

    return (
        <>
            {triggerButton}
            <PanelComponent
                headerLeft="Customize view"
                headerRight={panelHeaderRight}
                body={panelBody}
                footer={panelFooter}
            />
        </>
    );
}
